/**
 * Cards in the air: a round dealt out seat by seat, and the two cards a draw
 * takes off the stock.
 *
 * The server has already moved every card by the time a view arrives, so none
 * of this changes what anyone holds. It decides what the table holds back
 * while the cards travel, and the counts catch up as each one lands.
 */

import type { GameView, PlayerView } from "@ryzun/haf-saloon-game-engine";
import { findSelf, getActiveHand } from "./play.js";
import type { SeatOrientation } from "./seating.js";

/** How long one card takes to reach a seat. */
export const FLIGHT_MS = 320;

/** The gap between the cards of a single draw. */
export const DRAW_STAGGER_MS = 110;

/** The share of a deal the Hands take; the Feet go out in the rest. */
const HAND_SHARE = 0.75;

/** How far from the dealer's seat toward the middle their cards leave from. */
const DEALER_REACH = 0.35;

export type FlightTarget = "foot" | "hand";

export interface Flight {
    readonly playerId: string;
    readonly target: FlightTarget;
    /** When the card leaves, from the start of the deal. */
    readonly delayMs: number;
}

/** Everything still on its way to a seat. */
export interface Airborne {
    /** Cards on their way to each player's active hand, by player id. */
    readonly cards: ReadonlyMap<string, number>;
    /** The viewer's own cards still on their way, in the order they land. */
    readonly ownIds: readonly string[];
    /** Players whose Foot has not been set down yet. */
    readonly feet: ReadonlySet<string>;
}

export interface Point {
    readonly x: number;
    readonly y: number;
}

/** A stock draw: who took it, and how many cards came off. */
export interface StockDraw {
    readonly playerId: string;
    readonly count: number;
}

export const NOTHING_AIRBORNE: Airborne = { cards: new Map(), ownIds: [], feet: new Set() };

/**
 * Each seat's hand is turned to face its side of the table, measured clockwise
 * from the viewer's own seat. Kept in step with the rotations in card-hand.css.
 */
const SEAT_TURNS: Readonly<Record<SeatOrientation, number>> = {
    south: 0,
    southwest: 45,
    west: 90,
    northwest: 135,
    north: 180,
    northeast: 225,
    east: 270,
    southeast: 315
};

export function getSeatTurn(orientation: SeatOrientation): number {
    return SEAT_TURNS[orientation];
}

/** The shortest way round from one angle to another, so no card spins the long way. */
export function getTurnBetween(from: number, to: number): number {
    const delta = (((to - from) % 360) + 360) % 360;

    return delta > 180 ? delta - 360 : delta;
}

/** The middle of a box, measured from the corner of the layer the cards fly in. */
export function getCenter(box: DOMRectReadOnly, layer: DOMRectReadOnly): Point {
    return {
        x: box.left + (box.width / 2) - layer.left,
        y: box.top + (box.height / 2) - layer.top
    };
}

/**
 * Where the dealer's cards leave from: a little in front of their seat rather
 * than on it, so the card they deal themselves still travels.
 */
export function getDealOrigin(seat: Point, middle: Point): Point {
    return {
        x: seat.x + ((middle.x - seat.x) * DEALER_REACH),
        y: seat.y + ((middle.y - seat.y) * DEALER_REACH)
    };
}

/**
 * The deal, card by card: one to each player in turn, starting on the dealer's
 * left and ending with the dealer, until every Hand is full. Each Foot then
 * goes out whole, the way a counted stack is pushed across.
 *
 * Every card lands by the end of the window, however many players there are -
 * a bigger table only deals faster.
 */
export function planDeal(view: GameView, windowMs: number): readonly Flight[] {
    const seats = view.players.length;
    const order = view.players.map((_, step) => view.players[(view.dealerIndex + 1 + step) % seats]);
    const passes = Math.max(0, ...order.map((player) => player.handCount));
    const handCards = Array.from({ length: passes }, (_, pass) => order
        .filter((player) => pass < player.handCount)
        .map((player) => player.id))
        .flat();
    const feet = order.filter((player) => player.footCount > 0);

    const span = Math.max(windowMs - FLIGHT_MS, 0);
    const handSpan = span * HAND_SHARE;
    const handGap = handCards.length > 0 ? handSpan / handCards.length : 0;
    const footGap = feet.length > 0 ? (span - handSpan) / feet.length : 0;

    return [
        ...handCards.map((playerId, index): Flight => ({
            playerId,
            target: "hand",
            delayMs: Math.round(index * handGap)
        })),
        ...feet.map((player, index): Flight => ({
            playerId: player.id,
            target: "foot",
            delayMs: Math.round(handSpan + (index * footGap))
        }))
    ];
}

/** Everything a fresh deal has in the air before the first card leaves. */
export function getDealtAirborne(view: GameView): Airborne {
    return {
        cards: new Map(view.players.map((player) => [player.id, player.handCount])),
        ownIds: getActiveHand(findSelf(view)).map((card) => card.id),
        feet: new Set(view.players.filter((player) => player.footCount > 0).map((player) => player.id))
    };
}

/**
 * Finds the stock draw that turns one view into the next: the same player's
 * turn, gone from drawing to playing, off the stock. Anything else - a pile
 * grab, a new turn, a new round - is not one.
 */
export function findStockDraw(previous: GameView, next: GameView): StockDraw | null {
    const before = previous.turn;
    const after = next.turn;
    const count = previous.stockCount - next.stockCount;

    if (before === null || after === null || previous.round !== next.round || count <= 0) {
        return null;
    }

    if (before.playerId !== after.playerId || before.phase !== "draw" || after.phase !== "play" || after.drawnFrom !== "stock") {
        return null;
    }

    return { playerId: after.playerId, count };
}

/** Puts a draw's cards in the air, the viewer's own by id so they drop in as they land. */
export function addDraw(airborne: Airborne, previous: GameView, next: GameView, draw: StockDraw): Airborne {
    const isViewer = draw.playerId === next.viewerId;
    const held = new Set(getActiveHand(findSelf(previous)).map((card) => card.id));
    const drawn = isViewer
        ? getActiveHand(findSelf(next)).map((card) => card.id).filter((id) => !held.has(id))
        : [];

    return {
        ...airborne,
        cards: new Map([...airborne.cards, [draw.playerId, (airborne.cards.get(draw.playerId) ?? 0) + draw.count]]),
        ownIds: [...airborne.ownIds, ...drawn]
    };
}

/** Sets one card down at its seat. */
export function land(airborne: Airborne, flight: Pick<Flight, "playerId" | "target">, viewerId: string): Airborne {
    if (flight.target === "foot") {
        return { ...airborne, feet: new Set([...airborne.feet].filter((id) => id !== flight.playerId)) };
    }

    const left = (airborne.cards.get(flight.playerId) ?? 0) - 1;
    const cards = left > 0
        ? new Map([...airborne.cards, [flight.playerId, left]])
        : new Map([...airborne.cards].filter(([id]) => id !== flight.playerId));

    return {
        ...airborne,
        cards,
        ownIds: flight.playerId === viewerId ? airborne.ownIds.slice(1) : airborne.ownIds
    };
}

export function isGrounded(airborne: Airborne): boolean {
    return airborne.cards.size === 0 && airborne.feet.size === 0;
}

/** A player as the table should show them while some of their cards are still in the air. */
export function holdBack(player: PlayerView, airborne: Airborne): PlayerView {
    const inAir = airborne.cards.get(player.id) ?? 0;
    const footInAir = airborne.feet.has(player.id);

    if (inAir === 0 && !footInAir) {
        return player;
    }

    return {
        ...player,
        handCount: player.inFoot ? player.handCount : Math.max(player.handCount - inAir, 0),
        footCount: footInAir ? 0 : player.inFoot ? Math.max(player.footCount - inAir, 0) : player.footCount
    };
}
